import { Field, Form, Formik, FormikValues } from "formik";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import * as Yup from "yup";
import { client } from "../../../../App";
import {
  CustomerSearchType,
  enumToArray,
  UserRoleEnum,
} from "../../../../helpers/enums";
import {
  composedName,
  GetCustomerTransformToProfileImpersonationTable,
  getValidationRule,
  toInputUppercase,
} from "../../../../helpers/helpers";
import { IMPERSONIFICATION_SEARCH_USER } from "../../../../helpers/queries";
import { searchUserReq } from "../../../../models";
import {
  removePersistence,
  SearchUser,
  setImpersonateToken,
  setIssuers,
  setSearchUserResult,
  unsetImpersonation,
} from "../../../../reducers/auth";
import "./DashboardImpersonationSearch.scss";

import { hideLoading, showLoading } from "../../../../reducers/app";
import { clearStateContract } from "../../../../reducers/contracts";
import NoResult from "../../../common/NoResult/NoResult";
import DashboardImpersonationLinks from "../DashboardImpersonationLinks/DashboardImpersonationLinks";
import DashboardImpersonationResult from "../DashboardImpersonationResult/DashboardImpersonationResult";

const options = enumToArray(CustomerSearchType);
const TypeSelectComponent = (props: any) => {
  const handleChange = (value: any) => {
    props.onChange("type", value);
    props.setTypeSelect(value);
  };
  const handleBlur = () => {
    props.onBlur("type", true);
  };
  return (
    <div className="input-group-prepend">
      <Select
        id="search-type-selector"
        classNamePrefix="custom_type_search"
        options={options}
        onChange={handleChange}
        onBlur={handleBlur}
        value={props.value}
        isSearchable={false}
      />
      {!!props.error && props.touched && (
        <p
          className="form-error-text paragraph-small-form"
          role="alert"
          id={"search-type-selector-error"}
        >
          {props.error}
        </p>
      )}
    </div>
  );
};

const NoResultImpersonificate = (props: { t: any; isOpen: any }) => {
  const { t, isOpen } = props;
  return (
    isOpen && (
      <div className="empty">
        <NoResult text={t("errors.user.not.found")} />
      </div>
    )
  );
};

const DashboardImpersonationSearch = (props: any) => {
  // const {setNoResultError} = props;
  const [typeSelect, setTypeSelect] = useState(options[0]);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [noResult, setNoResult] = useState(false);
  const [internalNoResult, setInternalNoResult] = useState(noResult);

  const searchProfileData = useSelector((state: any) => state.auth.searchUser);
  const reloadSearchUser = useSelector(
    (state: any) => state.auth.reloadSearchUser
  );
  const { impersonationActive } = useSelector((state: any) => state.auth);

  const [isImpersonating, setIsImpersonating] = useState(false);

  const [foundUser, setFoundUser] = useState<SearchUser | null>(null);
  const schemaDashboardImpersonationSearch = (typeSelect: any, t: any) => {
    const validationEmail = getValidationRule("email");
    const validationFiscalCode = getValidationRule("fiscalCode");
    const validationCodeOrVat = getValidationRule("fiscalCodeOrVAT");

    switch (typeSelect.value) {
      case "EMAIL":
        return Yup.object().shape({
          value: Yup.string()
            .trim(t("common.empty_characters"))
            .strict(true)
            .matches(
              validationEmail,
              t("dashboard.impersonation.search.email.match")
            )
            .required(t("dashboard.impersonation.search.email.match")),
          type: Yup.string().required(),
        });
      case "EMAIL_SPID":
        return Yup.object().shape({
          value: Yup.string()
            .trim(t("common.empty_characters"))
            .strict(true)
            .matches(
              validationEmail,
              t("dashboard.impersonation.search.email.match")
            )
            .required(t("dashboard.impersonation.search.email.match")),
          type: Yup.string().required(),
        });

      case "FISCAL_CODE":
        return Yup.object().shape({
          value: Yup.string()
            .min(11, t("errors.registration.admin.fc.short"))
            .max(16, t("errors.registration.admin.fc.long"))
            .trim(t("common.empty_characters"))
            .strict(true)
            .test(
              "test-fiscalCode",
              t("errors.registration.admin.fc.match"),
              (value) => {
                if (value === undefined) {
                  return true;
                }
                if (value && value.length >= 12 && value.length <= 16) {
                  const fiscalCodeRegex = new RegExp(validationFiscalCode);
                  return fiscalCodeRegex.test(value);
                }
                return true;
              }
            )
            .required(t("errors.registration.admin.fc.required")),
        });

      case "PIVA":
        return Yup.object().shape({
          value: Yup.string()
            .min(11, t("errors.registration.admin.fc.short"))
            .max(16, t("errors.registration.admin.fc.long"))
            .trim(t("common.empty_characters"))
            .strict(true)
            .test(
              "test-Vat",
              t("errors.registration.admin.piva.match"),
              (value) => {
                if (value === undefined) {
                  return true;
                }
                if (value && value.length === 11) {
                  const vatRegex = new RegExp(validationCodeOrVat);
                  return vatRegex.test(value);
                }
                return false;
              }
            )
            .required(t("errors.registration.admin.fc.required")),
        });

      default:
        return Yup.object().shape({
          value: Yup.string()
            .trim(t("common.empty_characters"))
            .strict(true)
            .matches(
              validationEmail,
              t("dashboard.impersonation.search.email.match")
            )
            .required(t("dashboard.impersonation.search.email.required")),
          type: Yup.string().required(),
        });
    }
  };
  const searchRequest = (values: any) => {
    if (values.type.value === "PIVA") {
      let valueModified = "FISCAL_CODE";
      return client().query({
        query: IMPERSONIFICATION_SEARCH_USER,
        variables: searchUserReq(values.value, valueModified),
      });
    } else {
      return client().query({
        query: IMPERSONIFICATION_SEARCH_USER,
        variables: searchUserReq(values.value, values.type.value),
      });
    }
  };
  const handleSearch = useCallback(
    async (values: any) => {
      // const actions = formikProps;
      dispatch(clearStateContract());
      dispatch(unsetImpersonation());
      dispatch(removePersistence());
      dispatch(showLoading());
      setNoResult(false);
      return await searchRequest(values)
        .then((res: any) => {
          const { data } = res;
          let currentData = null;
          if (data) {
            if ("searchUser" in data) {
              if ("customer" in data.searchUser) {
                currentData = data.searchUser.customer;
              }
              if ("operator" in data.searchUser) {
                currentData = data.searchUser.operator;
              }
              const {
                username,
                fiscalCode,
                safoAccountId,
                email,
                role,
                name,
                surname,
                accountDetail: { emailVerified, status },
                impersonateToken,
                isCondominiumAdminDisabled,
                authIssuer,
              } = currentData;
              if (impersonateToken) {
                dispatch(setImpersonateToken(impersonateToken));
              }
              dispatch(
                setSearchUserResult({
                  username,
                  fiscalCode,
                  email,
                  role,
                  name,
                  surname,
                  safoAccountId,
                  emailVerified,
                  status,
                  impersonateToken,
                  isCondominiumAdminDisabled,
                  authIssuer,
                })
              );
              dispatch(setIssuers(authIssuer));
            }
            dispatch(hideLoading());
          }
        })
        .catch((e: any) => {
          dispatch(hideLoading());
          console.log("ERROR: ", e);
          // actions.setFieldError("value", "Error");
          // setNoResultError(true);
          setNoResult(true);
        });
      // .finally(() => actions.setSubmitting(false));
    },
    [dispatch]
  );

  useEffect(() => {
    if (reloadSearchUser.status) {
      const payload = {
        value: reloadSearchUser.email,
        type: { value: "EMAIL" },
      };
      handleSearch(payload)
        .then(() => {
          dispatch(reloadSearchUser({ email: null, status: false }));
        })
        .catch((e) => console.log(e));
    }
  }, [reloadSearchUser, dispatch, handleSearch]);
  useEffect(() => {
    if (searchProfileData) {
      setFoundUser(searchProfileData);
    }
  }, [searchProfileData, foundUser, setFoundUser]);
  console.log(searchProfileData);
  useEffect(() => {
    setIsImpersonating(impersonationActive);
  }, [impersonationActive, setIsImpersonating]);
  const box1 = [
    {
      arrow: t(
        "dashboard.impersonation.remote_control.link_access_reserved_area"
      ),
      link: "/dashboard",
    },
    searchProfileData &&
    searchProfileData.role !== UserRoleEnum.MUNICIPAL_USER &&
    searchProfileData.role !== UserRoleEnum.MUNICIPAL_OPERATOR &&
    searchProfileData.role === UserRoleEnum.CONDOMINIUM_ADMIN &&
    searchProfileData.isCondominiumAdminDisabled !== true
      ? {
          arrow: t(
            "dashboard.impersonation.remote_control.link_access_associate_contract"
          ),
          link: "/contracts/add",
        }
      : null,
    searchProfileData &&
    searchProfileData.role !== UserRoleEnum.MUNICIPAL_USER &&
    searchProfileData.role !== UserRoleEnum.MUNICIPAL_OPERATOR
      ? searchProfileData.role === UserRoleEnum.CONDOMINIUM_ADMIN
        ? null
        : {
            arrow: t(
              "dashboard.impersonation.remote_control.link_access_dissociation_contract"
            ),
            link: "/contracts/dissociate",
          }
      : null,
    searchProfileData &&
    (searchProfileData.role !== UserRoleEnum.MUNICIPAL_USER ||
      searchProfileData.role !== UserRoleEnum.MUNICIPAL_OPERATOR) &&
    searchProfileData.role === UserRoleEnum.CONDOMINIUM_ADMIN &&
    searchProfileData.isCondominiumAdminDisabled !== true
      ? {
          arrow: t(
            "dashboard.impersonation.remote_control.link_access_activation_online_bill"
          ),
          link: "/online-bills",
        }
      : null,
  ];
  const box2 = [
    {
      arrow: t("dashboard.impersonation.manage_user_web.reset_password"),
      type: "change-password",
      action: true,
    },
    {
      arrow: t("dashboard.impersonation.manage_user_web.modify_email"),
      link: "/operator/modify-email",
      type: "modify-email",
      action: false,
    },
    {
      arrow: t("dashboard.impersonation.manage_user_web.resend_email"),
      type: "resend-email",
      action: true,
    },
  ];
  const box3 = [
    {
      arrow: t("dashboard.impersonation.another_action.self_reading"),
      link: "/autolettura",
    },
  ];
  useEffect(() => {
    setInternalNoResult(noResult);
  }, [noResult]);
  return (
    <div
      className="DashboardImpersonationSearch"
      data-testid="DashboardImpersonationSearch"
    >
      <div className="container component">
        <div className="row">
          <div className="col-sm-12 col-md-12">
            <h2 className="headline-bold-fs5">
              {t("dashboard.manager.search-user.title")}
            </h2>
          </div>
        </div>
        <div className="row">
          <div className="col-sm-12 col-md-12">
            <label
              className="paragraph-small-fs9"
              htmlFor="container-select-search"
            >
              {t("dashboard.manager.search-user.description")}
            </label>
          </div>
        </div>
        <Formik
          initialValues={{ type: typeSelect, value: "" }}
          enableReinitialize={true}
          validationSchema={schemaDashboardImpersonationSearch(typeSelect, t)}
          onSubmit={(value: FormikValues, props: any) => handleSearch(value)}
        >
          {({
            errors,
            touched,
            submitCount,
            isSubmitting,
            values,
            setFieldValue,
            setFieldTouched,
            status,
          }) => (
            <Form>
              <div className="row d-search">
                <div className="col-sm-12 col-md-12 container-select-search">
                  <div
                    className={
                      errors.value
                        ? "input-group-prepend-container select-error"
                        : "input-group-prepend-container"
                    }
                  >
                    <TypeSelectComponent
                      inError={!!status}
                      value={values.type}
                      onChange={setFieldValue}
                      onBlur={setFieldTouched}
                      error={errors.type}
                      touched={touched.type}
                      setTypeSelect={setTypeSelect}
                    />
                    <Field
                      id="container-select-search"
                      aria-describedby={
                        errors.value && touched.value && submitCount > 0
                          ? "container-select-search-error"
                          : null
                      }
                      name={"value"}
                      type="text"
                      autoComplete="off"
                      className={"form-control input-type-search"}
                      placeholder={
                        values.type.value === "EMAIL"
                          ? t("placeholder.searhc.user.email")
                          : values.type.value === "EMAIL_SPID"
                          ? t("placeholder.searhc.user.email")
                          : values.type.value === "PIVA"
                          ? t("search.users.placeholder.piva")
                          : t("search.users.placeholder.fc")
                      }
                      onInput={
                        values.type.value === "FISCAL_CODE"
                          ? toInputUppercase
                          : null
                      }
                    />
                  </div>
                  {errors.value && touched.value && submitCount > 0 ? (
                    <p
                      className="form-error-text paragraph-small-form"
                      role="alert"
                      id={"container-select-search-error"}
                    >
                      {errors.value}
                    </p>
                  ) : null}
                </div>
                <div className="btn-container col-12">
                  <button
                    className={"btn btn-primary"}
                    type="submit"
                    disabled={isSubmitting}
                  >
                    {t("dashboard.manager.search-user.search-button")}
                  </button>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
      {internalNoResult ? (
        <NoResultImpersonificate isOpen={internalNoResult} t={t} />
      ) : foundUser !== null ? (
        <div className="container-impersonation">
          <div className="container">
            <DashboardImpersonationResult
              composedName={composedName(foundUser.name, foundUser.surname)}
              dataTable={GetCustomerTransformToProfileImpersonationTable(
                t,
                foundUser
              )}
            />
            <DashboardImpersonationLinks
              box1={box1}
              box2={box2}
              box3={box3}
              searchProfileData={foundUser}
              impersonationActive={isImpersonating}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default DashboardImpersonationSearch;
